import type { MutableRefObject, ReactNode, WheelEvent } from 'react';
import clsx from '../../utils/clsx';
import styles from '../../styles/WeaponRack.module.css';

interface WeaponRackProps {
  /** LoadoutMenu and the search and filter controls. */
  toolbar?: ReactNode;
  /** The plates. */
  children: ReactNode;
  /** "＋ Arm a weapon" - pinned below the drum, always in reach. */
  addSlot?: ReactNode;
  /** The clipboard bar, when something is on it. */
  footer?: ReactNode;
  /** Shown in place of the plates when the combatant carries nothing. */
  emptyHint?: ReactNode;
  isEmpty: boolean;
  onBackgroundClick?: () => void;
  /**
   * The drum. The VIEWPORT is fixed and clipped; the plate column inside
   * it is translated by the tab so the focused plate sits at the
   * viewport's vertical centre. These two refs are how the tab measures,
   * and `onWheel` is how a mouse wheel turns the cylinder.
   */
  viewportRef?: MutableRefObject<HTMLDivElement | null>;
  onWheel?: (e: WheelEvent) => void;
  /**
   * Park the drum as a plain top-down list. Used while filtering: the
   * visible plates are a subset with their real indices, and search
   * results read better flat.
   */
  flat?: boolean;
}

/**
 * The Armoury rack - LAYOUT ONLY.
 *
 * Follows CombatantStatusTab's convention: every panel-specific piece
 * arrives as a slot, so this file has no `if (actor)` and no context. It
 * owns the listbox semantics and where things sit.
 *
 * THE RACK IS A DRUM, NOT A SCROLLING LIST (v8.192.0). The viewport is
 * `overflow: clip` - deliberately not a scroll container - and the plate
 * column moves within it, so the weapon in focus is always anchored at
 * the middle of the panel and the rest wrap away above and below it.
 * Navigation is by wheel, arrow keys, or clicking a plate; there is no
 * scrollbar because the ensemble turning IS the scroll.
 *
 * The add affordance lives OUTSIDE the viewport, pinned below it. On the
 * old scrolling list it sat at the end of the rack, which with forty
 * weapons meant scrolling past all of them to arm another.
 *
 * `role="listbox"` with `role="option"` plates is the shape
 * CombatantGallery established, and ADR 2026-07-27 records that those
 * semantics are load-bearing for existing `getByRole` assertions.
 *
 * NOTHING FIXED-POSITIONED MAY BE RENDERED AS A CHILD OF A PLATE - the
 * plates are `container-type` elements and would become the containing
 * block for it. The context menu is the tab's job, portalled to body.
 */
export default function WeaponRack({
  toolbar,
  children,
  addSlot,
  footer,
  emptyHint,
  isEmpty,
  onBackgroundClick,
  viewportRef,
  onWheel,
  flat,
}: WeaponRackProps) {
  return (
    <>
      {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      <div
        className={styles.rack}
        // A click on empty rack space is a mouse convenience, not a control.
        role="presentation"
        onClick={onBackgroundClick}
        onWheel={onWheel}
        ref={viewportRef}
        data-testid="weapon-rack"
      >
        <div
          className={clsx(styles.plates, flat && styles.flat)}
          role="listbox"
          aria-label="Weapon rack"
          aria-orientation="vertical"
        >
          {children}
        </div>
        {isEmpty && emptyHint && <div className={styles.emptyHint}>{emptyHint}</div>}
      </div>
      {addSlot && <div className={styles.addRow}>{addSlot}</div>}
      {footer}
    </>
  );
}
